import { useEffect, useRef, useState } from "react";

import { Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useParams, useSearchParams } from "react-router-dom";

import { CommandLinkLandingLayout } from "./CommandLinkLandingLayout";
import {
  ClientAppEvent,
  COMMAND_LINK_TOKEN_QUERY_PARAM_KEY,
  CommandLinkName,
} from "../../constants";
import { SUBMIT_QUOTE_INTENT_QUERY_PARAM_KEY } from "./pages/SubmitQuoteLanding/constants";
import { SubmitQuoteLanding } from "./pages/SubmitQuoteLanding/SubmitQuoteLanding";
import { UpdateCustomerNotificationPreferenceLanding } from "./pages/UpdateCustomerNotificationPreferenceLanding";
import { VoluntellActivationLanding } from "./pages/VoluntellActivationLanding";

export function CommandLinkLanding() {
  const { commandName } = useParams<{ commandName: string }>();
  const [searchParams] = useSearchParams();
  /**
   * A landing strips this param from the URL as soon as it mounts
   * (docs/engineering/design/carrier_submit_quote_command_link_design.md §10), so the
   * URL stops being a place the token can be read from part-way through the visit.
   * The first non-null read is therefore latched and every later render answers from
   * the latch.
   *
   * Without it an authenticated visitor never sees the page. `useSearchParams` re-reads
   * `window.location` on any react-router navigation, and the signed-in tree issues
   * several on this route while its contexts settle — a trace of one logged-in load
   * showed six `pushState`s landing after the strip. Each one turned the token null and
   * rendered "Invalid link" over a page whose preview had already succeeded. Logged out
   * the navigations do not happen, which is why this only ever reproduced with a
   * session. `useLoadPath` (hooks/useLoadPath.ts) already declines to redirect this
   * route; these navigations come from elsewhere in the authenticated tree, so the read
   * has to be able to survive them rather than depend on nobody navigating.
   */
  const [token, setToken] = useState(() =>
    searchParams.get(COMMAND_LINK_TOKEN_QUERY_PARAM_KEY),
  );
  /**
   * The submit-quote landing strips `intent` along with the token, so it is
   * latched the same way and travels with the link it arrived on.
   */
  const [intent, setIntent] = useState(() =>
    searchParams.get(SUBMIT_QUOTE_INTENT_QUERY_PARAM_KEY),
  );
  const tokenInUrl = searchParams.get(COMMAND_LINK_TOKEN_QUERY_PARAM_KEY);
  if (tokenInUrl !== null && tokenInUrl !== token) {
    // Only ever fires if a second link is opened without a full page load; the
    // strip can take the token out of the URL but never puts a different one in.
    setToken(tokenInUrl);
    setIntent(searchParams.get(SUBMIT_QUOTE_INTENT_QUERY_PARAM_KEY));
  }
  const posthog = usePostHog();
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    if (!token && !hasLoggedRef.current) {
      hasLoggedRef.current = true;
      posthog.capture(ClientAppEvent.COMMAND_LINK_LANDING_WITHOUT_TOKEN, {
        commandName,
      });
    }
  }, [token, posthog, commandName]);

  if (!token) {
    return (
      <CommandLinkLandingLayout>
        <Typography color="error" textAlign="center">
          Invalid link. Please check the link sent to your email or contact
          support.
        </Typography>
      </CommandLinkLandingLayout>
    );
  }

  switch (commandName) {
    case CommandLinkName.ACTIVATE_VOLUNTELL_USER:
      return (
        <CommandLinkLandingLayout>
          <VoluntellActivationLanding token={token} />
        </CommandLinkLandingLayout>
      );
    case CommandLinkName.UPDATE_CUSTOMER_NOTIFICATION_PREFERENCE:
      return (
        <CommandLinkLandingLayout maxWidth="1000px">
          <UpdateCustomerNotificationPreferenceLanding token={token} />
        </CommandLinkLandingLayout>
      );
    case CommandLinkName.SUBMIT_QUOTE:
      return (
        <CommandLinkLandingLayout maxWidth="1000px" marginTop="1rem">
          <SubmitQuoteLanding token={token} intent={intent} />
        </CommandLinkLandingLayout>
      );
    default:
      return (
        <CommandLinkLandingLayout>
          <Typography color="error" textAlign="center">
            Invalid Link. Please check the link sent to your email or contact
            support.
          </Typography>
        </CommandLinkLandingLayout>
      );
  }
}
